/**
 * useSubscription — Custom React hook for subscription state.
 *
 * Aggregates data from multiple subscription endpoints via React Query.
 * Exposes helper predicates: isPro, isEnterprise, isFree, isNearLimit,
 * requiresUpgrade(featureId).
 */

import { useQuery } from "@tanstack/react-query";
import {
  getSubscriptionOverview,
  getPlanHistory,
  getUsageAnalytics,
  getSeats,
  getEntitlements,
  type PlanName,
} from "../api/subscriptions";

const STALE_MS = 60_000; // 60 seconds

// ── Main hook ─────────────────────────────────────────────────────────────────

export function useSubscription() {
  const overviewQuery = useQuery({
    queryKey: ["subscriptions", "overview"],
    queryFn: getSubscriptionOverview,
    staleTime: STALE_MS,
  });

  const overview = overviewQuery.data;
  const plan: PlanName = overview?.plan ?? "free";

  const isFree = plan === "free";
  const isStarter = plan === "starter";
  const isGrowth = plan === "growth";
  const isEnterprise = plan === "enterprise";
  /** Any paid tier (Starter, Growth or Enterprise). */
  const isPaid = !isFree;
  /** @deprecated alias kept for older call sites; means "on a paid plan". */
  const isPro = isPaid;

  function isNearLimit(metric: "repos" | "members" | "scansThisMonth"): boolean {
    if (!overview) return false;
    const { current, limit } = overview.usage[metric];
    if (limit === -1) return false;
    return current / Math.max(limit, 1) >= 0.8;
  }

  function isAtLimit(metric: "repos" | "members" | "scansThisMonth"): boolean {
    if (!overview) return false;
    const { current, limit } = overview.usage[metric];
    if (limit === -1) return false;
    return current >= limit;
  }

  function requiresUpgrade(featureId: string): boolean {
    if (!overview) return false;
    const unlockedFeatures = overview.limits.features ?? [];
    return !unlockedFeatures.includes(featureId);
  }

  return {
    // Raw query state
    overviewQuery,
    overview,

    // Derived plan predicates
    plan,
    isFree,
    isStarter,
    isGrowth,
    isEnterprise,
    isPaid,
    isPro,

    // Usage helpers
    isNearLimit,
    isAtLimit,
    requiresUpgrade,

    // Loading / error
    isLoading: overviewQuery.isLoading,
    isError: overviewQuery.isError,
    error: overviewQuery.error,
  };
}

// ── Supplementary hooks (lazy-loaded per tab) ──────────────────────────────

export function usePlanHistory() {
  return useQuery({
    queryKey: ["subscriptions", "history"],
    queryFn: getPlanHistory,
    staleTime: STALE_MS,
    select: (d) => d.history,
  });
}

export function useUsageAnalytics() {
  return useQuery({
    queryKey: ["subscriptions", "analytics"],
    queryFn: getUsageAnalytics,
    staleTime: STALE_MS,
  });
}

export function useSeats() {
  return useQuery({
    queryKey: ["subscriptions", "seats"],
    queryFn: getSeats,
    staleTime: STALE_MS,
  });
}

export function useEntitlements() {
  return useQuery({
    queryKey: ["subscriptions", "entitlements"],
    queryFn: getEntitlements,
    staleTime: STALE_MS,
  });
}
